import Meta from '../../components/Meta'
import { FC, useMemo, useRef, useState } from 'react'
import Select from 'react-select'
import {
  Collection_Slot,
  Order_By,
  useDeleteCollectionSlotMutation,
  useGetAllCollectionSlotsQuery,
} from '../../generated/urql.administrator'
import { Button, ButtonGroup, Spinner } from 'react-bootstrap'
import {
  CalendarPlus,
  Calendar3,
  PencilSquare,
  Trash,
} from 'react-bootstrap-icons'
import { InfiniteTable } from '../../components/InfiniteTable'
import CollectionSlotModal, {
  CollectionSlotModalType,
} from '../../components/CollectionSlotModal'
import moment from 'moment'
import CollectionSlotGeneratorModal from '../../components/CollectionSlotGeneratorModal'

const pageTitle = 'Terminarz'
const minDate = moment().toDate().toISOString()

const CollectionSlots: FC<{ id: number; readOnly?: boolean }> = ({
  id,
  readOnly,
}) => {
  const [showGenerateSlotsModal, setShowGenerateSlotsModal] = useState(false)
  const [selectedDay, setSelectedDay] = useState<SelectDayOption>()
  const collectionSlotModalRef = useRef<CollectionSlotModalType>(null)
  const [, deleteCollectionSlot] = useDeleteCollectionSlotMutation()
  const [{ data, fetching: isLoading }, loadSlots] =
    useGetAllCollectionSlotsQuery({
      pause: !id,
      variables: {
        collectionPointId: id,
        minDate,
        orderBy: { dateFrom: Order_By.Asc },
      },
    })
  const collectionSlots = data?.collection_slot || []

  const { days, byDay } = useMemo(
    () => prepareSlotsData(collectionSlots),
    [collectionSlots]
  )

  const daySlots = useMemo(
    () =>
      selectedDay && selectedDay.value in byDay ? byDay[selectedDay.value] : [],
    [selectedDay, byDay]
  )

  const onCloseGenerateSlotsModal = () => {
    onReLoadSlots()
    setShowGenerateSlotsModal(false)
  }

  const onReLoadSlots = () => loadSlots({ requestPolicy: 'network-only' })

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      <Meta title={pageTitle} />
      <div className="mt-3 mb-3 d-flex justify-content-between">
        <Select
          value={selectedDay}
          options={days}
          placeholder="Proszę wybrać dzień"
          isDisabled={!days.length}
          styles={{ control: (base) => ({ ...base, width: 280 }) }}
          onChange={(a) =>
            setSelectedDay(a ? { label: a.label, value: a.value } : undefined)
          }
        />
        <div className="d-flex gap-2">
          <Button
            className="d-flex gap-2 align-items-center"
            disabled={readOnly}
            onClick={() =>
              collectionSlotModalRef.current?.show({
                collectionPointId: id,
              })
            }
          >
            <CalendarPlus size={20} />
            Dodaj
          </Button>
          <Button
            variant="success"
            className="d-flex gap-2 align-items-center"
            disabled={readOnly}
            onClick={() => setShowGenerateSlotsModal(true)}
          >
            <Calendar3 size={20} />
            Generuj
          </Button>
        </div>
      </div>
      {!!selectedDay && (
        <InfiniteTable<SelectSlotOption>
          data={daySlots}
          columns={[
            {
              Header: 'Termin',
              accessor: 'label',
            },
            {
              Header: '',
              id: 'actions',
              accessor: (rowData) => (
                <div className="d-flex justify-content-end">
                  <ButtonGroup>
                    <Button
                      variant="primary"
                      disabled={readOnly}
                      onClick={() =>
                        collectionSlotModalRef.current?.show({
                          id: rowData.value,
                          collectionPointId: id,
                        })
                      }
                    >
                      <PencilSquare size={18} />
                    </Button>
                    <Button
                      variant="danger"
                      disabled={readOnly}
                      onClick={async () => {
                        if (window.confirm('Czy napewno chcesz usunąć slot?')) {
                          await deleteCollectionSlot({ id: rowData.value })
                          onReLoadSlots()
                        }
                      }}
                    >
                      <Trash size={18} />
                    </Button>
                  </ButtonGroup>
                </div>
              ),
            },
          ]}
        />
      )}
      <CollectionSlotModal
        ref={collectionSlotModalRef}
        onChange={onReLoadSlots}
      />
      <CollectionSlotGeneratorModal
        isVisible={showGenerateSlotsModal}
        onClose={onCloseGenerateSlotsModal}
        collectionPointId={id}
      />
    </div>
  )
}

export default CollectionSlots

type WithOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
type SelectDayOption = { label: string; value: string }
type SelectSlotOption = { label: string; value: number }
type Slot = { from: string; to: string; id: number }

const prepareSlotsData = (
  slots: Array<Pick<Collection_Slot, 'id' | 'dateFrom' | 'dateTo'>>
) => {
  const tempByDay: Record<string, Slot[]> = {}
  const byDay: Record<string, SelectSlotOption[]> = {}

  for (const s of slots) {
    const start = moment(s.dateFrom)
    const end = moment(s.dateTo)

    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) continue

    let cursorStart = start.clone()
    const finalEnd = end.clone()

    while (cursorStart.isBefore(finalEnd)) {
      const dayKey = cursorStart.format('dddd, DD.MM.YYYY')
      const dayEnd = cursorStart.clone().endOf('day')
      const segmentEnd = moment.min(dayEnd, finalEnd)

      if (!tempByDay[dayKey]) tempByDay[dayKey] = []
      tempByDay[dayKey].push({
        from: cursorStart.format('HH:mm'),
        to: segmentEnd.format('HH:mm'),
        id: s.id,
      })

      cursorStart = dayEnd.clone().add(1, 'millisecond').startOf('day')
    }
  }

  for (const day of Object.keys(tempByDay)) {
    tempByDay[day].sort((a, b) => a.from.localeCompare(b.from))
    const dedup: (typeof byDay)[typeof day] = []
    let prev: (typeof tempByDay)[typeof day][number] | null = null
    for (const cur of tempByDay[day]) {
      if (!prev || prev.from !== cur.from || prev.to !== cur.to)
        dedup.push({ label: `${cur.from} - ${cur.to}`, value: cur.id })
      prev = cur
    }
    byDay[day] = dedup
  }

  const days = Object.keys(byDay)
    .sort((a, b) => {
      const pa = moment(a, 'dddd, DD.MM.YYYY')
      const pb = moment(b, 'dddd, DD.MM.YYYY')
      if (!pa.isValid() || !pb.isValid()) return a.localeCompare(b)
      return pa.valueOf() - pb.valueOf()
    })
    .map((d) => ({ label: d, value: d }))

  return { days, byDay }
}
